import { useState, useEffect, useCallback } from "react";
import { useReorderDragDrop } from "@/hooks/useReorderDragDrop";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { EditableCell } from "@/components/EditableCell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MoveDeliveryDialog } from "./delivery-round/MoveDeliveryDialog";
import { AddProductModal } from "./delivery-round/AddProductModal";
import { toast } from "sonner";
import { isWarehouseView } from "@/utils/permissions";
import {
  Menu,
  ArrowLeft,
  Printer,
  FileDown,
  Truck,
  PackageCheck,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  MoveRight,
  Save,
  X,
  RotateCcw,
  GripVertical,
  Pencil,
  Trash2,
  Plus,
  Lock,
  Unlock,
} from "lucide-react";

export default function DeliveryRoundDetail() {
  const { authAxios, token } = useAuth();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { roundId } = useParams();
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingPdf, setGeneratingPdf] = useState(null);
  const [expandedDelivery, setExpandedDelivery] = useState(null);

  // Reorder state — drag handlers extracted into useReorderDragDrop hook
  const [editMode, setEditMode] = useState(false);
  const [reorderList, setReorderList] = useState([]); // ordered list of order_ids during drag
  const {
    draggedId,
    dragOverId,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
  } = useReorderDragDrop(reorderList, setReorderList);

  // Move dialog state
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [movingDelivery, setMovingDelivery] = useState(null);
  const [otherRounds, setOtherRounds] = useState([]);
  const [loadingRounds, setLoadingRounds] = useState(false);
  const [movingTo, setMovingTo] = useState(null);

  // Per-delivery items edit state
  const [editingDeliveryOrderId, setEditingDeliveryOrderId] = useState(null);
  const [draftItems, setDraftItems] = useState([]);
  const [savingItems, setSavingItems] = useState(false);
  const [addProductOpen, setAddProductOpen] = useState(false);
  // Rename round
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [productQuery, setProductQuery] = useState("");
  const [productResults, setProductResults] = useState([]);

  const isWarehouse = isWarehouseView(user);
  const isAdmin = user?.role === "admin";
  const basePath = isWarehouse ? "/warehouse" : user?.role === "admin" ? "/admin" : "/agent";

  // Dopo l'export BBB, gli ordini sono FATTURATO: solo l'admin può modificarli.
  const isExported = !!round?.bbb_exported_at;
  const canEditDelivery = isAdmin || (!isExported && isWarehouse);

  const fetchRound = useCallback(async () => {
    try {
      const res = await authAxios.get(`/delivery-rounds/${roundId}`);
      setRound(res.data);
    } catch {
      toast.error("Giro non trovato");
      navigate(`${basePath}/delivery-rounds`);
    } finally {
      setLoading(false);
    }
  }, [authAxios, roundId, navigate, basePath]);

  useEffect(() => {
    fetchRound();
  }, [fetchRound]);

  const saveRoundName = async () => {
    const trimmed = nameDraft.trim().slice(0, 80);
    if (trimmed === (round?.name || "")) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      const res = await authAxios.patch(`/delivery-rounds/${roundId}/name`, { name: trimmed || null });
      setRound(res.data);
      toast.success(trimmed ? "Nome aggiornato" : "Nome rimosso");
      setEditingName(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento nome");
    } finally {
      setSavingName(false);
    }
  };

  const [numberMap, setNumberMap] = useState({}); // order_id -> typed delivery_number (free)

  const enterEditMode = () => {
    // Init reorderList with current order (sorted by delivery_number)
    const sorted = [...(round.deliveries || [])].sort((a, b) => a.delivery_number - b.delivery_number);
    setReorderList(sorted.map(d => d.order_id));
    // Init numberMap with each current delivery_number — user can edit freely.
    const map = {};
    for (const d of sorted) map[d.order_id] = d.delivery_number;
    setNumberMap(map);
    setEditMode(true);
  };

  // Drag & drop handlers
  // Set free-form delivery number (no auto-reshuffle).
  // The visual list stays as-is; user can press "Riordina" to sort by typed numbers.
  const handleSetNumber = (order_id, raw) => {
    const val = raw === "" ? "" : Math.max(0, Math.floor(Number(raw) || 0));
    setNumberMap(prev => ({ ...prev, [order_id]: val }));
  };

  // Sort the visual list by typed numbers (ascending). Ties keep current order.
  const applyRiordina = () => {
    setReorderList(prev => {
      const arr = [...prev];
      arr.sort((a, b) => {
        const na = Number(numberMap[a]) || 0;
        const nb = Number(numberMap[b]) || 0;
        return na - nb;
      });
      return arr;
    });
    toast.success("Lista riordinata per numero");
  };

  const saveReorder = async () => {
    try {
      // Send the typed numbers as delivery_numbers (free-form), preserving visual order on ties
      const delivery_order = reorderList.map((order_id, i) => ({
        order_id,
        delivery_number: Number(numberMap[order_id]) || (i + 1),
      }));
      const res = await authAxios.put(`/delivery-rounds/${roundId}/reorder`, { delivery_order });
      setRound(res.data);
      setEditMode(false);
      toast.success("Ordine consegne aggiornato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel riordino");
    }
  };

  const openMoveDialog = async (delivery) => {
    setMovingDelivery(delivery);
    setMoveDialogOpen(true);
    setLoadingRounds(true);
    try {
      const res = await authAxios.get("/delivery-rounds");
      setOtherRounds(res.data.filter((r) => r.id !== roundId));
    } catch {
      setOtherRounds([]);
    } finally {
      setLoadingRounds(false);
    }
  };

  const handleMove = async (targetRoundId) => {
    if (!movingDelivery) return;
    setMovingTo(targetRoundId);
    try {
      const res = await authAxios.put(`/delivery-rounds/${roundId}/move-delivery`, {
        order_id: movingDelivery.order_id,
        target_round_id: targetRoundId,
      });
      setRound(res.data);
      setMoveDialogOpen(false);
      setMovingDelivery(null);
      toast.success(`Consegna spostata con successo`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nello spostamento");
    } finally {
      setMovingTo(null);
    }
  };

  const handleRemoveFromRound = async (delivery) => {
    const confirmMsg = `Rimuovere ${delivery.client_name} dal giro ${round?.letter || ""}? L'ordine tornerà in "Da evadere".`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await authAxios.put(`/delivery-rounds/${roundId}/remove-delivery`, {
        order_id: delivery.order_id,
      });
      setRound(res.data);
      toast.success(`Ordine ${delivery.order_numero || ""} rimandato in Da Evadere`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella rimozione");
    }
  };

  // ===== Delivery items edit =====
  const startEditItems = (delivery) => {
    setEditingDeliveryOrderId(delivery.order_id);
    setDraftItems((delivery.items || []).map(it => ({
      product_id: it.product_id,
      product_name: it.product_name,
      product_sku: it.product_sku || "",
      quantity: Number(it.quantity) || 0,
      qty_omaggio: Number(it.qty_omaggio) || 0,
      unit: it.unit || "pz",
      price_unit: Number(it.price_unit) || 0,
      sc1: Number(it.sc1) || 0, sc2: Number(it.sc2) || 0,
      sc3: Number(it.sc3) || 0, sc4: Number(it.sc4) || 0,
      iva_percentage: Number(it.iva_percentage) || 10,
    })));
  };
  const cancelEditItems = () => {
    setEditingDeliveryOrderId(null);
    setDraftItems([]);
    setAddProductOpen(false);
    setProductQuery("");
    setProductResults([]);
  };
  const updateDraftItem = (idx, field, value) => {
    setDraftItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };
  const removeDraftItem = (idx) => {
    setDraftItems(prev => prev.filter((_, i) => i !== idx));
  };
  const saveEditItems = async () => {
    if (!editingDeliveryOrderId) return;
    const items = draftItems
      .filter(it => (Number(it.quantity) || 0) > 0 || (Number(it.qty_omaggio) || 0) > 0)
      .map(it => ({
        product_id: it.product_id,
        product_name: it.product_name,
        product_sku: it.product_sku || "",
        quantity: Number(it.quantity) || 0,
        qty_omaggio: Number(it.qty_omaggio) || 0,
        unit: it.unit || "pz",
        price_unit: Number(it.price_unit) || 0,
        sc1: Number(it.sc1) || 0, sc2: Number(it.sc2) || 0,
        sc3: Number(it.sc3) || 0, sc4: Number(it.sc4) || 0,
        iva_percentage: Number(it.iva_percentage) || 10,
      }));
    if (items.length === 0) {
      toast.error("Almeno un articolo con quantità > 0");
      return;
    }
    setSavingItems(true);
    try {
      await authAxios.put(`/orders/${editingDeliveryOrderId}/items`, { items });
      const r2 = await authAxios.get(`/delivery-rounds/${roundId}`);
      setRound(r2.data);
      cancelEditItems();
      toast.success("Articoli aggiornati");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingItems(false);
    }
  };

  // Catalog product search
  useEffect(() => {
    if (!addProductOpen) return;
    const q = productQuery.trim();
    if (q.length < 2) { setProductResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await authAxios.get(`/products?search=${encodeURIComponent(q)}`);
        setProductResults(res.data.slice(0, 40));
      } catch { setProductResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [productQuery, addProductOpen, authAxios]);

  const addProductToDraft = (p) => {
    if (draftItems.some(it => it.product_id === p.id)) {
      toast.info("Articolo già presente");
      return;
    }
    setDraftItems(prev => [...prev, {
      product_id: p.id,
      product_name: p.name,
      product_sku: p.sku || "",
      quantity: 1,
      qty_omaggio: 0,
      unit: p.unit_of_measure || "pz",
      price_unit: Number(p.price_list) || 0,
      sc1: 0, sc2: 0, sc3: 0, sc4: 0,
      iva_percentage: Number(p.iva_percentage) || 10,
    }]);
    setAddProductOpen(false);
    setProductQuery("");
  };

  const handleUnmarkBbb = async () => {
    if (!window.confirm("Sicuro di voler ripristinare questo giro come NON fatturato?\n\nGli ordini che erano stati portati in stato FATTURATO dall'export BBB torneranno a stato RICEVUTO e potrai riesportarli.")) return;
    setGeneratingPdf("unbbb");
    try {
      const res = await authAxios.post(`/delivery-rounds/${roundId}/unmark-bbb`);
      const n = res.data?.n_restored ?? 0;
      toast.success(`Giro ripristinato: ${n} ordini riportati a RICEVUTO`);
      const r2 = await authAxios.get(`/delivery-rounds/${roundId}`);
      setRound(r2.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore ripristino BBB");
    } finally {
      setGeneratingPdf(null);
    }
  };

  const handleDownloadBbb = async () => {
    setGeneratingPdf("bbb");
    try {
      const res = await authAxios.post(`/delivery-rounds/${roundId}/export-bbb`, null, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `BBBdataZona_${round?.letter || "X"}_${round?.date || ""}.Txt`.replace(/\//g, "-");
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      // Refresh round to get bbb_exported_at timestamp
      const r2 = await authAxios.get(`/delivery-rounds/${roundId}`);
      setRound(r2.data);
      toast.success("File BBB scaricato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore export BBB");
    } finally {
      setGeneratingPdf(null);
    }
  };

  const _triggerDownload = (blobUrl, filename, openInline = false) => {
    const a = document.createElement("a");
    a.href = blobUrl;
    if (!openInline) {
      a.download = filename;
    }
    a.rel = "noopener";
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDownloadPdf = async (view) => {
    setGeneratingPdf(view);
    try {
      if (view === "preparazione") {
        const res = await authAxios.get(`/delivery-rounds/${roundId}/preparazione-pdf`, { responseType: "blob" });
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        _triggerDownload(url, `Preparazione_Giro_${round?.letter || "X"}.pdf`);
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } else {
        // Per Cliente / Lista Scarico: backend returns a pdf_url (static file)
        const res = await authAxios.get(`/delivery-rounds/${roundId}/pdf?view=${view}`);
        // Serve direttamente l'URL → <a> click (compatibile iOS, niente popup blocker)
        const pdfUrl = `${API.replace("/api", "")}${res.data.pdf_url}`;
        _triggerDownload(pdfUrl, res.data.filename || `giro_${round?.letter || "X"}_${view}.pdf`);
      }
    } catch (err) {
      // Estrai il messaggio dettagliato dal backend (utile per debug in produzione).
      // La risposta può essere un blob (preparazione) o JSON (per cliente / lista scarico).
      let detail = "";
      try {
        if (err?.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          try { detail = JSON.parse(text)?.detail || text; }
          catch { detail = text; }
        } else if (typeof err?.response?.data === "object") {
          detail = err.response.data.detail || JSON.stringify(err.response.data);
        } else {
          detail = err?.response?.data || err?.message || "";
        }
      } catch {/* ignore */}
      const msg = detail
        ? `Errore PDF: ${String(detail).slice(0, 240)}`
        : "Errore nella generazione del PDF";
      toast.error(msg);
    } finally {
      setGeneratingPdf(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500" />
      </div>
    );
  }

  if (!round) return null;

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="delivery-round-detail">
      <main className="p-4 sm:p-6 lg:p-8">
        <PageHeader title="Giro Consegne" />
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white shrink-0" onClick={() => navigate(`${basePath}/delivery-rounds`)}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Giri</span>
          </Button>
          <div className="flex-1 flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center shrink-0">
              <span className="text-orange-400 font-mono font-bold text-lg">{round.letter}</span>
            </div>
            <div className="min-w-0 flex-1">
              {editingName ? (
                <div className="flex items-center gap-1.5">
                  <input
                    autoFocus
                    type="text"
                    value={nameDraft}
                    maxLength={80}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveRoundName();
                      if (e.key === "Escape") setEditingName(false);
                    }}
                    placeholder="Nome del giro…"
                    className="flex-1 min-w-0 bg-[#09090b] border border-orange-500/40 rounded-md px-2 h-8 text-white text-sm"
                    data-testid="round-name-input"
                  />
                  <button
                    type="button"
                    onClick={saveRoundName}
                    disabled={savingName}
                    data-testid="round-name-save"
                    className="h-8 px-2 rounded-md bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-xs font-semibold"
                  >
                    OK
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingName(false)}
                    className="h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 text-zinc-300 flex items-center justify-center"
                    aria-label="Annulla"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h1 className="font-heading text-lg sm:text-xl font-bold text-white truncate">
                    Giro {round.letter}{round.name ? ` ${round.name}` : ""} del {round.date
                      ? new Date(round.date + "T00:00:00").toLocaleDateString("it-IT")
                      : new Date(round.created_at).toLocaleDateString("it-IT")}
                  </h1>
                  {(isAdmin || isWarehouse) && !isExported && (
                    <button
                      type="button"
                      onClick={() => { setNameDraft(round.name || ""); setEditingName(true); }}
                      className="shrink-0 p-1 rounded text-zinc-400 hover:text-orange-300 hover:bg-orange-500/10 transition-colors"
                      title="Rinomina giro"
                      data-testid="round-name-edit-btn"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
              <p className="text-xs text-zinc-500">{round.deliveries?.length} consegne</p>
            </div>
          </div>
        </div>

        {/* PDF + BBB Buttons */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <Button
            data-testid="pdf-preparazione-btn"
            className="bg-[#18181b] border border-white/10 hover:border-fuchsia-500/40 text-white h-auto py-3 justify-start"
            onClick={() => handleDownloadPdf("preparazione")}
            disabled={generatingPdf !== null}
          >
            <div className="flex items-center gap-2">
              <Printer className="w-4 h-4 text-fuchsia-400 shrink-0" />
              <div className="text-left min-w-0">
                <p className="font-medium text-sm">Preparazione</p>
                <p className="text-[10px] text-zinc-500 font-normal hidden sm:block">Foglio magazzino</p>
              </div>
            </div>
          </Button>

          <Button
            data-testid="pdf-client-btn"
            className="bg-[#18181b] border border-white/10 hover:border-amber-500/30 text-white h-auto py-3 justify-start"
            onClick={() => handleDownloadPdf("client")}
            disabled={generatingPdf !== null}
          >
            <div className="flex items-center gap-2">
              <Printer className="w-4 h-4 text-amber-400 shrink-0" />
              <div className="text-left min-w-0">
                <p className="font-medium text-sm">Per Cliente</p>
                <p className="text-[10px] text-zinc-500 font-normal hidden sm:block">Consegne compatte</p>
              </div>
            </div>
          </Button>

          <Button
            data-testid="pdf-picking-btn"
            className="bg-[#18181b] border border-white/10 hover:border-emerald-500/30 text-white h-auto py-3 justify-start"
            onClick={() => handleDownloadPdf("picking")}
            disabled={generatingPdf !== null}
          >
            <div className="flex items-center gap-2">
              <PackageCheck className="w-4 h-4 text-emerald-400 shrink-0" />
              <div className="text-left min-w-0">
                <p className="font-medium text-sm">Lista Scarico</p>
                <p className="text-[10px] text-zinc-500 font-normal hidden sm:block">Prodotti raggruppati</p>
              </div>
            </div>
          </Button>

          <Button
            data-testid="export-bbb-btn"
            className={`h-auto py-3 justify-start col-span-2 lg:col-span-1 ${
              round?.bbb_exported_at
                ? "bg-emerald-500/10 border border-emerald-500/40 hover:border-emerald-500/60 text-white"
                : "bg-[#18181b] border border-white/10 hover:border-blue-500/40 text-white"
            }`}
            onClick={handleDownloadBbb}
            disabled={generatingPdf !== null}
          >
            <div className="flex items-center gap-2">
              <FileDown className={`w-4 h-4 shrink-0 ${round?.bbb_exported_at ? "text-emerald-400" : "text-blue-400"}`} />
              <div className="text-left min-w-0">
                <p className="font-medium text-sm">File BBB</p>
                <p className="text-[10px] text-zinc-500 font-normal hidden sm:block">
                  {round?.bbb_exported_at
                    ? `Esportato ${new Date(round.bbb_exported_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}`
                    : "Fatturazione gestionale"}
                </p>
              </div>
            </div>
          </Button>
        </div>

        {/* Ripristino: giro erroneamente marcato come fatturato (solo admin) */}
        {isAdmin && round?.bbb_exported_at && (
          <div className="mb-6 -mt-3">
            <Button
              data-testid="unmark-bbb-btn"
              variant="outline"
              className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 h-auto py-2 text-xs"
              onClick={handleUnmarkBbb}
              disabled={generatingPdf !== null}
              title="Annulla la marcatura come 'fatturato' (BBB). Gli ordini tornano a RICEVUTO."
            >
              <RotateCcw className={`w-3.5 h-3.5 mr-1.5 ${generatingPdf === "unbbb" ? "animate-spin" : ""}`} />
              Ripristina come non fatturato
            </Button>
            <p className="text-[10px] text-zinc-500 mt-1 ml-1">
              Solo admin · usa se qualcuno ha premuto BBB per sbaglio.
            </p>
          </div>
        )}

        {/* Deliveries Header with Edit Toggle */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Consegne</h2>
          <div className="flex items-center gap-2">
            {editMode ? (
              <>
                <Button
                  data-testid="cancel-reorder-btn"
                  variant="ghost"
                  size="sm"
                  className="text-zinc-400 hover:text-white"
                  onClick={() => setEditMode(false)}
                >
                  <X className="w-4 h-4 mr-1" />
                  Annulla
                </Button>
                <Button
                  data-testid="reset-numbers-btn"
                  size="sm"
                  variant="ghost"
                  className="text-amber-300 hover:text-amber-200 border border-amber-500/30 hover:bg-amber-500/10"
                  onClick={() => {
                    const cleared = {};
                    for (const oid of reorderList) cleared[oid] = "";
                    setNumberMap(cleared);
                    toast.success("Numerazione azzerata — inserisci manualmente i nuovi numeri");
                  }}
                  title="Cancella tutti i numeri in arancione (puoi riscriverli manualmente)"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Azzera
                </Button>
                <Button
                  data-testid="sort-by-number-btn"
                  size="sm"
                  variant="ghost"
                  className="text-orange-300 hover:text-orange-200 border border-orange-500/30 hover:bg-orange-500/10"
                  onClick={applyRiordina}
                  title="Ordina la lista in base ai numeri digitati"
                >
                  <ArrowUpDown className="w-4 h-4 mr-1" />
                  Riordina
                </Button>
                <Button
                  data-testid="save-reorder-btn"
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={saveReorder}
                >
                  <Save className="w-4 h-4 mr-1" />
                  Salva
                </Button>
              </>
            ) : canEditDelivery ? (
              <Button
                data-testid="edit-order-btn"
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-white border border-white/10"
                onClick={enterEditMode}
              >
                <ArrowUpDown className="w-4 h-4 mr-1" />
                Numera
              </Button>
            ) : null}
          </div>
        </div>

        {/* Deliveries List */}
        <div className="space-y-1.5">
          {(editMode
            ? reorderList.map(oid => round.deliveries?.find(d => d.order_id === oid)).filter(Boolean)
            : round.deliveries
          )?.map((d, idx) => {
            const displayNum = editMode ? idx + 1 : d.delivery_number;
            const isDragOver = editMode && dragOverId === d.order_id && draggedId !== d.order_id;
            const isDragging = editMode && draggedId === d.order_id;
            const codFull = d.cod_destin ? `${d.client_codice_cli || ""}${d.cod_destin}` : null;
            return (
            <Card
              key={d.order_id}
              data-testid={`delivery-${d.delivery_number}`}
              draggable={editMode}
              onDragStart={editMode ? handleDragStart(d.order_id) : undefined}
              onDragOver={editMode ? handleDragOver(d.order_id) : undefined}
              onDragLeave={editMode ? handleDragLeave : undefined}
              onDrop={editMode ? handleDrop(d.order_id) : undefined}
              onDragEnd={editMode ? handleDragEnd : undefined}
              className={`bg-zinc-800 border transition-all shadow-md shadow-black/40 ${
                isDragging ? "opacity-40 border-amber-500/50" :
                isDragOver ? "border-amber-500/50 bg-amber-500/5 scale-[1.01]" :
                "border-zinc-700 hover:border-zinc-600 hover:shadow-lg hover:shadow-black/50 hover:-translate-y-px"
              } ${editMode ? "cursor-move" : ""}`}
            >
              <CardContent className="p-0">
                <div className="flex items-stretch gap-0">
                  {/* Drag handle + editable position in edit mode */}
                  {editMode && (
                    <div className="pl-1.5 py-1 shrink-0 flex items-center gap-1">
                      <GripVertical className="w-3 h-3 text-zinc-500" />
                      <input
                        type="number"
                        min={0}
                        value={numberMap[d.order_id] ?? ""}
                        data-testid={`delivery-pos-input-${d.order_id}`}
                        onClick={(e) => e.currentTarget.select()}
                        onChange={(e) => handleSetNumber(d.order_id, e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                        draggable={false}
                        onDragStart={(e) => e.stopPropagation()}
                        className="w-12 h-7 rounded-md bg-orange-500/10 text-orange-400 font-mono font-bold text-center outline-none border border-transparent focus:border-orange-500/60 focus:bg-orange-500/20 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                  )}

                  {/* Main content — compact row */}
                  <button
                    className="flex-1 flex items-center gap-2 px-2 py-1 text-left min-w-0"
                    onClick={() =>
                      !editMode && setExpandedDelivery(expandedDelivery === d.delivery_number ? null : d.delivery_number)
                    }
                  >
                    {!editMode && (
                      <div className="w-7 h-7 rounded-md bg-orange-500/10 flex items-center justify-center shrink-0">
                        <span className="text-orange-400 font-mono font-bold text-xs">{d.delivery_number}</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      {/* Inline layout: on lg+ screens (>=1024px) name + address + city on one row;
                          on smaller screens stack to 2 rows.
                          Use a fixed-width column for the client name on lg+ so the
                          address always starts at the same X position across rows. */}
                      <div className="flex flex-col lg:flex-row lg:items-baseline gap-x-3 gap-y-0.5 min-w-0">
                        {/* Cluster 1: client name + numero + codes (fixed col on lg+) */}
                        <div className="flex items-baseline gap-2 flex-wrap min-w-0 lg:w-[360px] lg:shrink-0">
                          <span className="text-white font-semibold text-sm truncate max-w-full">{d.client_name}</span>
                          <span className="text-[10px] text-zinc-500 font-mono shrink-0">#{d.order_numero}</span>
                          {codFull && (
                            <span className="text-[9px] text-cyan-300 font-mono bg-cyan-500/10 px-1 py-0.5 rounded shrink-0">
                              {codFull}
                            </span>
                          )}
                          {d.bbb_excluded && (
                            <span
                              data-testid={`bbb-locked-badge-${d.order_id}`}
                              title="Ordine bloccato: NON sarà incluso nell'export BBB"
                              className="text-[9px] font-bold font-mono bg-red-500/15 border border-red-500/40 text-red-300 rounded px-1 py-[1px] shrink-0"
                            >
                              BLOCCATO
                            </span>
                          )}
                        </div>
                        {/* Cluster 2: destinazione/indirizzo/città (flex-1 fills remaining row) */}
                        {(d.client_indirizzo || d.client_citta || d.destinazione_nome) && (
                          <div className="flex items-center gap-1.5 flex-wrap min-w-0 lg:flex-1" data-testid={`delivery-addr-${d.delivery_number}`}>
                            {d.destinazione_nome && (
                              <span className="text-[10px] text-amber-300 font-medium truncate">↳ {d.destinazione_nome}</span>
                            )}
                            {d.client_indirizzo && (
                              <span className="text-[11px] text-zinc-300 truncate">{d.client_indirizzo}</span>
                            )}
                            {d.client_citta && (
                              <span className="text-[10px] text-emerald-300 font-medium bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20 shrink-0">
                                {d.client_citta}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 self-center">
                      <div className="text-right leading-tight">
                        <p className="text-white font-mono font-bold text-sm">
                          {d.total?.toLocaleString("it-IT", { style: "currency", currency: "EUR" })}
                        </p>
                        <p className="text-[10px] text-zinc-500">{d.items?.length || 0} art.</p>
                      </div>
                      {!editMode && (
                        expandedDelivery === d.delivery_number
                          ? <ChevronUp className="w-4 h-4 text-zinc-500" />
                          : <ChevronDown className="w-4 h-4 text-zinc-500" />
                      )}
                    </div>
                  </button>

                  {/* Edit mode action buttons: move + remove */}
                  {editMode && (
                    <div className="pr-3 shrink-0 flex items-center gap-2">
                      <Button
                        data-testid={`move-delivery-${d.order_id}`}
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 text-zinc-400 hover:text-amber-400 border border-white/10 hover:border-amber-500/30"
                        onClick={() => openMoveDialog(d)}
                        title="Sposta in altro giro"
                      >
                        <MoveRight className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`remove-delivery-${d.order_id}`}
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 text-zinc-400 hover:text-red-400 border border-white/10 hover:border-red-500/30"
                        onClick={() => handleRemoveFromRound(d)}
                        title="Rimanda in Da Evadere"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Expanded items (hidden in edit mode) */}
                {!editMode && expandedDelivery === d.delivery_number && (
                  <div className="border-t border-white/5 px-4 py-3">
                    {/* Edit controls */}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-zinc-400 text-xs font-medium">
                        {editingDeliveryOrderId === d.order_id ? "Modifica articoli" : "Articoli"}
                      </span>
                      {editingDeliveryOrderId === d.order_id ? (
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={cancelEditItems}
                            className="h-7 px-2 text-xs text-zinc-400 hover:text-white" data-testid="cancel-edit-items">
                            <X className="w-3 h-3 mr-1" />Annulla
                          </Button>
                          <Button size="sm" onClick={() => { setAddProductOpen(true); setProductQuery(""); setProductResults([]); }}
                            className="h-7 px-2 text-xs bg-blue-500 hover:bg-blue-600 text-white" data-testid="add-article-btn">
                            <Plus className="w-3 h-3 mr-1" />Articolo
                          </Button>
                          <Button size="sm" onClick={saveEditItems} disabled={savingItems}
                            className="h-7 px-2 text-xs bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="save-edit-items">
                            <Save className="w-3 h-3 mr-1" />{savingItems ? "..." : "Salva"}
                          </Button>
                        </div>
                      ) : canEditDelivery ? (
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="ghost"
                            onClick={async () => {
                              const newVal = !d.bbb_excluded;
                              const msg = newVal
                                ? "Bloccare questo ordine? Non sarà esportato nel file BBB."
                                : "Sbloccare l'ordine? Tornerà ad essere esportato nel file BBB.";
                              if (!window.confirm(msg)) return;
                              try {
                                await authAxios.put(`/orders/${d.order_id}/bbb-exclusion`, { excluded: newVal });
                                setRound(prev => ({
                                  ...prev,
                                  deliveries: prev.deliveries.map(x =>
                                    x.order_id === d.order_id ? { ...x, bbb_excluded: newVal } : x
                                  )
                                }));
                                toast.success(newVal ? "Ordine bloccato dall'export BBB" : "Ordine sbloccato");
                              } catch (err) {
                                toast.error(err.response?.data?.detail || "Errore");
                              }
                            }}
                            className={`h-7 px-2 text-xs ${d.bbb_excluded ? "text-emerald-400 hover:text-emerald-300" : "text-red-400 hover:text-red-300"}`}
                            data-testid={`toggle-bbb-lock-${d.order_id}`}
                            title={d.bbb_excluded ? "Rimetti l'ordine nell'export BBB" : "Escludi l'ordine dall'export BBB"}
                          >
                            {d.bbb_excluded
                              ? (<><Unlock className="w-3 h-3 mr-1" />Sblocca</>)
                              : (<><Lock className="w-3 h-3 mr-1" />Blocca</>)
                            }
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => startEditItems(d)}
                            className="h-7 px-2 text-xs text-amber-400 hover:text-amber-300"
                            data-testid={`edit-items-${d.order_id}`}>
                            <Pencil className="w-3 h-3 mr-1" />Modifica
                          </Button>
                        </div>
                      ) : isExported ? (
                        <span className="text-[10px] text-emerald-500/70 font-mono">Fatturato (solo admin può modificare)</span>
                      ) : null}
                    </div>

                    {/* Modal: Aggiungi articolo */}
                    <AddProductModal
                      open={addProductOpen && editingDeliveryOrderId === d.order_id}
                      onClose={() => setAddProductOpen(false)}
                      productQuery={productQuery}
                      setProductQuery={setProductQuery}
                      productResults={productResults}
                      draftItems={draftItems}
                      onAdd={addProductToDraft}
                    />

                    {/* Items table */}
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left text-zinc-400 py-1.5 text-xs font-medium">Prodotto</th>
                          <th className="text-right text-zinc-400 py-1.5 text-xs font-medium w-14">Vnd</th>
                          <th className="text-right text-zinc-400 py-1.5 text-xs font-medium w-14">Om.</th>
                          <th className="text-right text-zinc-400 py-1.5 text-xs font-medium w-16">Prezzo</th>
                          <th className="text-right text-zinc-400 py-1.5 text-xs font-medium w-16">Tot</th>
                          {editingDeliveryOrderId === d.order_id && <th className="w-6"></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {(editingDeliveryOrderId === d.order_id ? draftItems : (d.items || [])).map((item, idx) => {
                          const isEdit = editingDeliveryOrderId === d.order_id;
                          const qty = Number(item.quantity) || 0;
                          const qtyOm = Number(item.qty_omaggio) || 0;
                          const price = Number(item.price_unit) || 0;
                          const sc1 = Number(item.sc1) || 0;
                          const sc2 = Number(item.sc2) || 0;
                          const sc3 = Number(item.sc3) || 0;
                          const sc4 = Number(item.sc4) || 0;
                          let netUnit = price;
                          for (const s of [sc1, sc2, sc3, sc4]) {
                            if (s > 0) netUnit *= (1 - s / 100);
                          }
                          const scParts = [sc1, sc2, sc3, sc4].filter(s => s > 0);
                          const scLabel = scParts.length ? `Sc ${scParts.map(s => `${s}`).join("+")}%` : null;
                          const lineTotal = qty * netUnit;
                          return (
                            <tr key={`${item.product_id || "noid"}-${item.product_sku || idx}`} className={`border-b border-white/5 ${idx % 2 === 1 ? "bg-white/[0.025]" : ""}`}>
                              <td className="py-1.5">
                                <span className="text-white text-xs">{item.product_name}</span>
                                {item.product_sku && <span className="text-zinc-500 text-[10px] ml-1">({item.product_sku})</span>}
                                {scLabel && (
                                  <span className="ml-1.5 text-[10px] text-amber-400 font-medium" data-testid={`item-sc-${idx}`}>{scLabel}</span>
                                )}
                              </td>
                              <td className="py-1.5 text-right">
                                {isEdit ? (
                                  <EditableCell value={qty} decimals={3}
                                    onChange={(v) => updateDraftItem(idx, "quantity", Math.max(0, v))}
                                    className="w-16 h-6 text-center bg-[#09090b] border-emerald-500/30 text-emerald-400 text-xs px-1" />
                                ) : (
                                  <span className="text-white font-mono text-xs">{qty}</span>
                                )}
                              </td>
                              <td className="py-1.5 text-right">
                                {isEdit ? (
                                  <EditableCell value={qtyOm} decimals={3}
                                    onChange={(v) => updateDraftItem(idx, "qty_omaggio", Math.max(0, v))}
                                    className="w-16 h-6 text-center bg-[#09090b] border-fuchsia-500/30 text-fuchsia-400 text-xs px-1" />
                                ) : (
                                  <span className={qtyOm > 0 ? "text-fuchsia-400 font-mono text-xs" : "text-zinc-600 font-mono text-xs"}>{qtyOm || "-"}</span>
                                )}
                              </td>
                              <td className="py-1.5 text-right">
                                {isEdit ? (
                                  <EditableCell value={price} decimals={2}
                                    onChange={(v) => updateDraftItem(idx, "price_unit", Math.max(0, v))}
                                    className="w-14 h-6 text-center bg-[#09090b] border-white/20 text-zinc-300 text-xs px-1" />
                                ) : (
                                  <div className="flex flex-col items-end leading-tight">
                                    {scParts.length > 0 && (
                                      <span className="text-zinc-500 line-through font-mono text-[10px]">{price.toFixed(2)}</span>
                                    )}
                                    <span className="text-zinc-200 font-mono text-xs">{netUnit.toFixed(2)}</span>
                                  </div>
                                )}
                              </td>
                              <td className="py-1.5 text-right text-white font-mono text-xs">{lineTotal.toFixed(2)}</td>
                              {isEdit && (
                                <td className="py-1.5 text-right">
                                  <button onClick={() => removeDraftItem(idx)}
                                    className="p-1 rounded text-zinc-500 hover:text-red-400"
                                    data-testid={`remove-item-${idx}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {d.note && !editingDeliveryOrderId && (
                      <p className="text-xs text-zinc-500 mt-2 italic">Note: {d.note}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
          })}

          {round.deliveries?.length === 0 && (
            <Card className="bg-[#18181b] border-white/5">
              <CardContent className="p-6 text-center">
                <p className="text-zinc-500">Nessuna consegna in questo giro</p>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Move Delivery Dialog */}
      <MoveDeliveryDialog
        open={moveDialogOpen}
        onOpenChange={setMoveDialogOpen}
        movingDelivery={movingDelivery}
        loadingRounds={loadingRounds}
        otherRounds={otherRounds}
        movingTo={movingTo}
        onMove={handleMove}
      />
    </div>
  );
}
